/* teads.ts */
import AnalyticsInstance, { AnalyticEventType } from "./analytics";
import CmpUtils from "./cmp";
import DeviceConfig from "@devices/config";
import Event from "@shared/classes/event";
import Genre from "@shared/classes/genre";
import { AdsWindow } from "@shared/types/interfaces/ads";
import { store } from "@shared/store/store";
import {
  clearPlayAd,
  setPauseAd,
  setPlayAd,
} from "@shared/store/slices/ads.slice";

/**
 * Constants & Flags
 */
const DEBUG = false;
const PLAYER_HEIGHT = 1080;
const PLAYER_WIDTH = 1920;
const DEFAULT_MIN_DURATION = 0;
const DEFAULT_MAX_DURATION = 60;

/**
 * Teads placement types
 */
export enum Placement {
  IN_PLAY = "inPlay",
  IN_PAUSE = "inPause",
}

/**
 * Teads utility functions
 */
const TeadsUtils = {
  buildContentMetadata: (asset?: Event) => {
    try {
      DEBUG && console.info("[Teads] Building content metadata");
      // Get the state from the store
      const { genres, media } = store.getState();
      const language = media.audio.language;
      const genre = genres.data?.find(
        (genre: Genre) => genre.id === asset?.genreId
      );
      // Build the metadata
      const metadata: {
        livestream: boolean;
        language?: typeof language;
        genre?: string;
        channel?: string;
      } = {
        livestream: media.live,
      };
      if (language) {
        metadata.language = language;
      }
      if (genre) {
        metadata.genre = genre?.title;
      }
      if (asset?.channel) {
        metadata.channel = asset?.channel?.title;
      }
      DEBUG && console.info("[Teads] Content metadata:", metadata);
      return metadata;
    } catch (error) {
      console.error("[Teads] Error building content metadata:", error);
      return { livestream: false };
    }
  },
  initializeTeads: ({
    asset,
    adsWindow,
    placementType,
    placementId,
    containerId,
    videoId,
  }: {
    asset?: Event;
    adsWindow?: AdsWindow;
    placementType: Placement;
    placementId: number;
    containerId: string;
    videoId?: string;
  }) => {
    try {
      DEBUG && console.info("[Teads] Initializing");

      const { info, seller } = DeviceConfig;
      const { userAgent } = window.navigator;
      const gdprConsent = CmpUtils.getGDPR();

      DEBUG &&
        console.info(
          "[Teads] Container:",
          document.getElementById(containerId)
        );
      DEBUG &&
        videoId &&
        console.info("[Teads] Video:", document.getElementById(videoId));

      // App context
      const appContext = {
        dom: {
          refElement: document.getElementById(containerId),
          ...(videoId
            ? { videoElement: document.getElementById(videoId) }
            : {}),
        },
        application: {
          appId: seller.bundleId,
          appStoreUrl: seller.storeUrl,
        },
        contentMetaData: {
          ...TeadsUtils.buildContentMetadata(asset),
        },
        device: {
          userAgent,
          deviceType: 3, // https://support.teads.tv/support/solutions/articles/36000574049-implementation-guide-csai-#deviceinfo - 3: Connected TV
          deviceId: info.uuid,
        },
        user: gdprConsent
          ? {
              gdpr: 1, // https://support.teads.tv/support/solutions/articles/36000574049-implementation-guide-csai-#gdpr - 1: GDPR applies
              gdprConsent,
            }
          : {},
      };

      // Placement context
      const placementContext = {
        placementId: placementId,
        playerHeight: PLAYER_HEIGHT,
        playerWidth: PLAYER_WIDTH,
        minDuration: adsWindow?.minDuration ?? DEFAULT_MIN_DURATION,
        maxDuration: adsWindow?.maxDuration ?? DEFAULT_MAX_DURATION,
      };

      // Create the SDK instance and initialize an InPlay placement
      const sdkInstance = window.teads?.SDK?.create(appContext);
      if (!sdkInstance) return {};
      const placementInstance =
        placementType === Placement.IN_PLAY
          ? sdkInstance.initInPlay(placementContext)
          : sdkInstance.initInPause(placementContext);

      // Placement events
      placementInstance?.addEventListener("adLoaded", function () {
        try {
          DEBUG && console.info("[Teads] InPlay: Ad loaded");
          switch (placementType) {
            case Placement.IN_PAUSE:
              AnalyticsInstance.getOmnitrackManager()
                ?.getPauseAdSession()
                ?.setPauseAdResponseAt(new Date().toISOString());
              AnalyticsInstance.getOmnitrackManager()
                ?.getPauseAdSession()
                ?.setWasAdShown(true);
              AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
              store.dispatch(setPauseAd({ active: true }));
              break;
            case Placement.IN_PLAY:
              store.dispatch(setPlayAd({ active: true }));
              break;
          }
          placementInstance.playAd();
        } catch (error) {
          console.error("[Teads] Error on adLoaded:", error);
        }
      });
      placementInstance?.addEventListener("adCompleted", function () {
        try {
          DEBUG && console.info("[Teads] InPlay: Ad playback completed");
          placementInstance.stopAd();
          switch (placementType) {
            case Placement.IN_PAUSE:
              break;
            case Placement.IN_PLAY:
              store.dispatch(clearPlayAd());
              break;
          }
        } catch (error) {
          console.error("[Teads] Error on adCompleted:", error);
        }
      });
      placementInstance?.addEventListener(
        "adFailedToLoad",
        function (event: any) {
          try {
            console.error(
              "[Teads] InPlay: Ad failed to load:",
              event?.detail?.error
            );
            switch (placementType) {
              case Placement.IN_PAUSE:
                AnalyticsInstance.getOmnitrackManager()
                  ?.getPauseAdSession()
                  ?.setPauseAdResponseAt(new Date().toISOString());
                AnalyticsInstance.getOmnitrackManager()
                  ?.getPauseAdSession()
                  ?.setWasAdShown(false);
                AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
                break;
              case Placement.IN_PLAY:
                store.dispatch(clearPlayAd());
                break;
            }
          } catch (error) {
            console.error("[Teads] Error on adFailedToLoad:", error);
          }
        }
      );
      placementInstance?.addEventListener("adError", function (event: any) {
        try {
          console.error("[Teads] InPlay: Ad error:", event?.detail?.error);
          switch (placementType) {
            case Placement.IN_PAUSE:
              AnalyticsInstance.getOmnitrackManager()
                ?.getPauseAdSession()
                ?.setPauseAdResponseAt(new Date().toISOString());
              AnalyticsInstance.getOmnitrackManager()
                ?.getPauseAdSession()
                ?.setWasAdShown(true);
              AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
              break;
            case Placement.IN_PLAY:
              store.dispatch(clearPlayAd());
              break;
          }
        } catch (error) {
          console.error("[Teads] Error on adError:", error);
        }
      });

      // Return the SDK instance and the placement instance
      return { sdkInstance, placementInstance };
    } catch (error) {
      console.error("[Teads] Error initializing Teads:", error);
      return {};
    }
  },
};

export default TeadsUtils;
